import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProductPointures } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge,
  NdoeStats, NdoeStatCard, NdoeError,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import useRefLists from './useRefLists';
import ReportFilters from './ReportFilters';
import { money, days, pct, dateTxt } from './format';
import './rapports.css';

const decisionBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.recommandation_tone} label={row.recommandation_label} />
);

// Écart en points entre la part des ventes et la part du stock, en barre divergente.
// À droite (or) = sous-stockée. À gauche (rouge) = sur-stockée.
const EcartBar = ({ ecart }) => {
  const width = `${Math.min(50, Math.abs(ecart) * 5)}%`;
  return (
    <div className="pt-ecart">
      <span className={`num pt-ecart__val ${ecart > 0 ? 'is-plus' : ecart < 0 ? 'is-moins' : ''}`}>
        {ecart > 0 ? '+' : ''}{ecart}
      </span>
      <div className="pt-bar" aria-hidden="true">
        <span className="pt-bar__axe" />
        {ecart !== 0 && (
          <i className={ecart > 0 ? 'is-plus' : 'is-moins'} style={{ width }} />
        )}
      </div>
    </div>
  );
};

const SORTS = [
  { value: 'vendu', label: 'Quantité vendue' },
  { value: 'ecart', label: 'Plus sous-stockée' },
  { value: 'couverture', label: 'Couverture la plus courte' },
  { value: 'stock', label: 'Plus grand stock' },
  { value: 'taille', label: 'Pointure croissante' },
];
const DECISIONS = [
  { value: '', label: 'Toutes les décisions' },
  { value: 'renforcer', label: 'À renforcer' },
  { value: 'reduire', label: 'À réduire' },
  { value: 'maintenir', label: 'À maintenir' },
];

const EMPTY_FILTERS = { marque: '', categorie: '', matiere: '', couleur: '', pointure: '', recommandation: '' };

export default function AnalysePointures() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  // Arrivée depuis un autre écran (ex. le drill « couleur → pointure » du rapport
  // des ventes) : on se pré-filtre sur la pointure passée en paramètre d'URL.
  const [searchParams] = useSearchParams();
  const pointureUrl = searchParams.get('pointure');

  const [period, setPeriod] = useState({ period: 90 });
  const [filters, setFilters] = useState(
    pointureUrl ? { ...EMPTY_FILTERS, pointure: Number(pointureUrl) || '' } : EMPTY_FILTERS,
  );
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('vendu');

  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const refs = useRefLists(isAdmin);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(q.trim()), 400);
    return () => clearTimeout(timer);
  }, [q]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
    ...filters, q: search, sort,
  }), [period, filters, search, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProductPointures(apiParams);
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || "Impossible de charger l'analyse des pointures.");
    } finally { setLoading(false); }
  }, [apiParams]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value ?? '' }));
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const seqMap = new Map((payload.sequence?.pointures || []).map((s) => [s.taille, s]));
      const columns = [
        { title: 'Pointure', value: (r) => r.taille },
        { title: 'Quantité vendue', value: (r) => r.quantite_vendue },
        { title: 'Part des ventes (%)', value: (r) => r.part_ventes },
        { title: 'Stock', value: (r) => r.stock_actuel },
        { title: 'Part du stock (%)', value: (r) => r.part_stock },
        { title: 'Écart (points)', value: (r) => r.ecart },
        { title: 'Couverture (jours)', value: (r) => r.couverture_jours ?? '' },
        { title: "Taux d'écoulement (%)", value: (r) => r.taux_ecoulement },
        { title: 'Références en rupture', value: (r) => r.ruptures },
        { title: 'Références qui stagnent', value: (r) => r.dormantes },
        { title: "Chiffre d'affaires", value: (r) => r.chiffre_affaires },
        { title: 'Décision', value: (r) => r.recommandation_label },
        { title: 'Raison', value: (r) => r.raison },
        {
          title: "Rang d'écoulement après arrivage",
          value: (r) => { const s = seqMap.get(r.taille); return s ? `${s.rang_moyen} (${s.arrivages} arrivages)` : ''; },
        },
        { title: 'Top produits', value: (r) => r.top_produits.map((p) => `${p.nom} (${p.vendu})`).join(' | ') },
      ];
      const { start_date: from, end_date: to } = payload.meta || {};
      downloadCsv(`analyse-pointures_${from || ''}_${to || ''}.csv`, toCsv(columns, payload.data || []));
      toast.success(`${(payload.data || []).length} pointure(s) exportée(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const { summary = {}, meta = {}, data = [], sequence = {} } = payload;
  const seqPointures = sequence.pointures || [];
  const seqRangMax = seqPointures.reduce((m, p) => Math.max(m, p.rang_moyen), 1);
  const seqRangMin = seqPointures.reduce((m, p) => Math.min(m, p.rang_moyen), seqRangMax);

  const columns = [
    {
      title: 'Pointure', dataIndex: 'taille', width: 96,
      render: (t, r) => (
        <div className="pt-taille">
          <b>P{t}</b>
          <small>{r.variantes} réf.</small>
        </div>
      ),
    },
    {
      title: 'Vendu', dataIndex: 'quantite_vendue', align: 'right', width: 122,
      render: (v, r) => (
        <div className="pt-duo">
          <b className="num">{v}</b>
          <small className="num">{pct(r.part_ventes)} des ventes</small>
        </div>
      ),
    },
    {
      title: 'Stock', dataIndex: 'stock_actuel', align: 'right', width: 122,
      render: (v, r) => (
        <div className="pt-duo">
          <b className="num">{v}</b>
          <small className="num">{pct(r.part_stock)} du stock</small>
        </div>
      ),
    },
    {
      title: 'Écart ventes / stock', dataIndex: 'ecart', width: 172,
      render: (v) => <EcartBar ecart={v} />,
    },
    { title: 'Couverture', dataIndex: 'couverture_jours', align: 'right', width: 110, render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Ruptures', dataIndex: 'ruptures', align: 'right', width: 92, render: (v) => <span className="num">{v || '—'}</span> },
    { title: 'Stagnantes', dataIndex: 'dormantes', align: 'right', width: 108, render: (v) => <span className="num">{v || '—'}</span> },
    { title: 'Décision', dataIndex: 'recommandation', width: 172, render: (_, r) => decisionBadge(r) },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.taille}
      title={`Pointure ${r.taille}`}
      subtitle={`${r.variantes} référence(s) · ${r.ruptures} en rupture`}
      badge={decisionBadge(r)}
      metrics={[
        { label: 'Vendu', value: `${r.quantite_vendue} (${pct(r.part_ventes)})` },
        { label: 'Stock', value: `${r.stock_actuel} (${pct(r.part_stock)})` },
        { label: 'Écart', value: `${r.ecart > 0 ? '+' : ''}${r.ecart} pts` },
        { label: 'Couverture', value: days(r.couverture_jours) },
      ]}
      onClick={() => setDetail(r)}
    />
  );

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Analyse pointures"
        subtitle="Quelles pointures renforcer, lesquelles réduire — en comparant la part des ventes à la part du stock."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>
        )}
      </div>

      <NdoeStats className="pp-stats">
        <KpiCard label="Pointure vedette" value={summary.pointure_vedette ? `P${summary.pointure_vedette}` : '—'} gold />
        <KpiCard label="Paires vendues" value={summary.paires_vendues ?? 0} />
        <KpiCard label="Paires en stock" value={summary.paires_en_stock ?? 0} />
        <KpiCard label="À renforcer" value={summary.a_renforcer ?? 0}
          onClick={() => setFilter('recommandation')('renforcer')} />
        <KpiCard label="À réduire" value={summary.a_reduire ?? 0}
          onClick={() => setFilter('recommandation')('reduire')} />
        <KpiCard label="Références en rupture" value={summary.ruptures ?? 0} />
      </NdoeStats>

      <p className="pp-note rp-note">
        <NdoeStatusBadge kind="custom" tone="info" label="Comment lire" />
        {' '}Une pointure qui pèse <b>plus dans les ventes que dans le stock</b> est sous-stockée&nbsp;: c'est
        l'<b>écart</b>, en points. Les parts sont calculées sur <b>tout le mix</b>, même quand le tableau est filtré.
        La <b>couverture</b> se projette sur le rythme des <b>30 derniers jours</b>, pas sur la moyenne de la période.
      </p>

      <ReportFilters
        q={q} setQ={setQ}
        filters={filters} setFilter={setFilter}
        refs={refs}
        sort={sort} setSort={setSort} sorts={SORTS}
        activeCount={activeCount}
      >
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Décision" style={{ minWidth: 190 }} options={DECISIONS}
            value={filters.recommandation} onChange={setFilter('recommandation')} />
        </div>
      </ReportFilters>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          className="pp-table"
          data={data}
          columns={columns}
          renderCard={renderCard}
          rowKey="taille"
          loading={loading}
          error={error}
          onRetry={load}
          scroll={{ x: 1100 }}
          onRow={(record) => ({ onClick: () => setDetail(record), style: { cursor: 'pointer' } })}
          pagination={false}
          empty={{
            icon: '📐',
            title: 'Aucune pointure à analyser',
            text: 'Aucune vente ni stock sur ce périmètre. Élargis la période ou retire des filtres.',
          }}
        />
      )}

      {seqPointures.length > 0 && (
        <section className="sq-bloc">
          <h3 className="ve-titre">Séquence d'écoulement après arrivage</h3>
          <p className="pp-note rp-note">
            <NdoeStatusBadge kind="custom" tone="info" label="FIFO" />
            {' '}Quand un modèle arrive en plusieurs pointures le même jour, laquelle part en premier&nbsp;?
            Rang moyen de première vente, mesuré sur <b>{sequence.arrivages_analyses} arrivage(s) réel(s)</b>
            {' '}multi-pointures — tout l'historique tracé, indépendamment de la période et des filtres.
            <b> Rang 1 = la pointure qui part en premier.</b>
          </p>
          <div className="sq-liste">
            {seqPointures.map((p) => {
              const largeur = seqRangMax === seqRangMin
                ? 100
                : Math.round(12 + 88 * ((seqRangMax - p.rang_moyen) / (seqRangMax - seqRangMin)));
              return (
                <div className="sq-item" key={p.taille}>
                  <b>P{p.taille}</b>
                  <div className="sq-bar" aria-hidden="true"><i style={{ width: `${largeur}%` }} /></div>
                  <small className="num">rang {p.rang_moyen} · {p.arrivages} arrivages</small>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={520}
        title={detail ? `Pointure ${detail.taille}` : 'Détail pointure'}
        className="pp-drawer"
      >
        {detail && (
          <div className="pp-detail">
            <div className="pp-detail__badges">
              {decisionBadge(detail)}
              {detail.ruptures > 0 && <NdoeStatusBadge kind="custom" tone="danger" label={`${detail.ruptures} en rupture`} />}
              {detail.dormantes > 0 && <NdoeStatusBadge kind="custom" tone="neutral" label={`${detail.dormantes} stagnantes`} />}
            </div>

            <div className="rp-raison">
              <span className="rp-raison__label">Pourquoi cette décision</span>
              <p>{detail.raison}</p>
            </div>

            <NdoeStats>
              <NdoeStatCard label="Part des ventes" value={pct(detail.part_ventes)} gold />
              <NdoeStatCard label="Part du stock" value={pct(detail.part_stock)} />
              <NdoeStatCard label="Écart" value={`${detail.ecart > 0 ? '+' : ''}${detail.ecart} pts`} />
              <NdoeStatCard label="Vendu" value={detail.quantite_vendue} />
              <NdoeStatCard label="Stock" value={detail.stock_actuel} />
              <NdoeStatCard label="Couverture" value={days(detail.couverture_jours)} />
              <NdoeStatCard label="Chiffre d'affaires" value={money(detail.chiffre_affaires)} />
              <NdoeStatCard label="Taux d'écoulement" value={pct(detail.taux_ecoulement)} />
            </NdoeStats>

            <h4 className="pp-detail__title">Produits qui tirent cette pointure</h4>
            {detail.top_produits.length ? (
              <div className="pp-detail__pt">
                <div className="pp-detail__pt-row pp-detail__pt-row--head">
                  <span>Produit</span><span>Vendu</span><span />
                </div>
                {detail.top_produits.map((p) => (
                  <div className="pp-detail__pt-row" key={p.nom}>
                    <span>{p.nom}</span>
                    <span className="num">{p.vendu}</span>
                    <span />
                  </div>
                ))}
              </div>
            ) : (
              <p className="pp-detail__warn">Aucune vente sur cette pointure pendant la période.</p>
            )}

            <h4 className="pp-detail__title">Références</h4>
            <ul className="pp-detail__dates">
              <li><span>Références concernées</span><b>{detail.variantes}</b></li>
              <li><span>En rupture (vendaient, stock 0)</span><b>{detail.ruptures}</b></li>
              <li><span>Qui stagnent (stock, aucune vente)</span><b>{detail.dormantes}</b></li>
            </ul>
          </div>
        )}
      </Drawer>
    </NdoePage>
  );
}
